<script>
    import '@aurodesignsystem/auro-icon';
    export let transitionFn = () => {},
        transitionProps = {};
    // Type of modal (default or slider), used for styling
    export let type = 'default';
    // If using a custom close button, provide a ref so the modal can focus it
    export let closeButtonRef = null;
    let isOpen = false;
    let closeButton, triggerElement;
    let outsideElements = [];

    // List of focusable elements -- will need to explicitly add new Auro custom elements
    const focusableElementsQuery =
        'button, [href], input, ods-inputtext, select, textarea, [tabindex]:not([tabindex="-1"]), auro-button, ods-button';

    export async function open() {
        triggerElement = document.activeElement;
        // Prevent focusing on elements outside the modal
        outsideElements = document.querySelectorAll(focusableElementsQuery);
        outsideElements.forEach(element =>
            element.setAttribute('tabIndex', '-1')
        );

        document.body.classList.add('modal-open');
        isOpen = true;
    }

    export function close() {
        // Note: will override original tabIndex. May cause issues if tabIndex was already set
        outsideElements.forEach(element => element.removeAttribute('tabIndex'));
        isOpen = false;
        document.body.classList.remove('modal-open');
        triggerElement.focus();
    }

    // Called after transition finishes to prevent layout jank
    function focusCloseButton() {
        if (closeButtonRef) {
            closeButtonRef.focus();
        } else {
            closeButton.focus();
        }
    }

    function handleKeydown(e) {
        var key = e.key || e.keyCode;
        if (key === 'Escape' || key === 'Esc' || key === 27) {
            close();
        }
    }

    // This is a Svelte Action
    // See https://svelte.dev/docs#use_action
    function hideOtherElements(node) {
        // Hide direct children of body from screen readers
        const elementsToHide = [
            ...document.querySelectorAll('body > *')
        ].filter(element => !element.hasAttribute('aria-hidden'));
        elementsToHide.forEach(element =>
            element.setAttribute('aria-hidden', 'true')
        );
        // Move modal to end of body so it is not hidden
        document.body.appendChild(node);

        return {
            destroy() {
                elementsToHide.forEach(element =>
                    element.removeAttribute('aria-hidden')
                );
            }
        };
    }

    function positionBelowHeader(node) {
        const mowHeaderHeight = 52;
        // If the user scrolled the page, do not account for the header
        // This is used to set a CSS variable to use in positioning
        let sliderTopPosition =
            mowHeaderHeight - window.scrollY > 0
                ? mowHeaderHeight - window.scrollY
                : 0;
        node.style = `--slider-top: ${sliderTopPosition}px`;
    }
</script>

<svelte:window on:keydown={handleKeydown} />
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    class="wrapper"
    class:closed={!isOpen}
    class:slider-wrapper={type == 'slider'}
    on:click={close}
/>
{#if isOpen}
    <div
        aria-modal="true"
        aria-labelledby="modal-label"
        class:modal={type != 'slider'}
        class:slider={type == 'slider'}
        transition:transitionFn={transitionProps}
        on:introend={focusCloseButton}
        data-testid="modal"
        use:hideOtherElements
        use:positionBelowHeader
    >
        <div class:header={type != 'slider'}>
            <!-- 
                Button should be first on page so screen readers don't miss the heading
                We can use CSS to reverse the order visually
             -->
            <slot name="action">
                <button
                    data-testid="close-button"
                    on:click={close}
                    bind:this={closeButton}
                    type="button"
                >
                    <span>
                        <auro-icon category="interface" name="x-lg">
                    </span>
                    <span class="util_displayHiddenVisually">Close</span>
                </button>
                <span id="modal-label">
                    <slot name="header" />
                </span>
            </slot>
        </div>
        <slot />
    </div>
{/if}

<style lang="scss">
    .modal {
        position: absolute;
        top: 20%;
        left: 50%;
        transform: translateX(-50%);
        background-color: var(--ds-basic-color-page-background-default);
        padding: var(--ds-grid-gutter-lg);
        max-width: 33rem;
        width: calc(100% - 2rem);
    }

    .slider {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        position: fixed;
        overflow-y: scroll;
        background-color: var(--ds-basic-color-page-background-default);
        padding: var(--ds-grid-margin-md);
        z-index: 1; // prevent auro-flightline from appearing on top

        @media screen and (min-width: $ds-grid-breakpoint-md) {
            width: 50%;
            left: auto;
            top: var(--slider-top);
            max-width: 38rem;
        }
    }

    .modal button {
        background: none;
        border: 0;
        color: var(--ds-basic-color-texticon-muted);
        padding: 0;
        height: var(--ds-grid-gutter-lg);
        cursor: pointer;
        box-sizing: content-box;
        position: relative;
        border-radius: 5px;
        /* 
            Only apply focus styles when user is tabbing 
            :global is required because Svelte will remove rule otherwise
        */
        :global(&.focus-visible:focus) {
            border: 1px solid var(--ds-basic-color-border-inverse);
            background: var(--ds-basic-color-surface-accent2);
            color: var(--ds-basic-color-texticon-inverse);
            outline: none;
        }
        :global(&.focus-visible:focus::after) {
            content: '';
            display: block;
            position: absolute;
            top: -0.1875rem;
            bottom: -0.1875rem;
            left: -0.1875rem;
            right: -0.1875rem;
            border-radius: 5px;
            border: 1px solid var(--ds-basic-color-surface-accent2);
        }
    }

    .wrapper {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--ds-advanced-color-accents-transparency);
        opacity: 1;
        transition: opacity 0.3s;

        &.slider-wrapper {
            background-color: var(--ds-basic-color-page-background-default);

            @media screen and (min-width: $ds-grid-breakpoint-md) {
                background-color: var(--ds-advanced-color-accents-transparency);
            }
        }

        &.closed {
            opacity: 0;
            pointer-events: none;
        }
    }

    .header {
        display: flex;
        flex-direction: row-reverse;
        align-items: baseline;
        justify-content: space-between;
    }

    :global(body.modal-open) {
        overflow-y: hidden;
    }
</style>
